/**
 * Blunder Test Suite
 * Curated collection of known-bad AI positions for regression testing.
 * Storage: localStorage (dev-only tool, no Firebase needed).
 */

// ── Data Models ──

export interface BlunderTestCase {
  id: string;
  name: string;
  description: string;
  fen: string;
  lightKing: string;
  darkKing: string;
  testSide: 'light' | 'dark';
  blunderMove?: { from: string; to: string };
  expectedNote: string;
  severity: 'critical' | 'major' | 'minor';
  sourceGameId?: string;
  sourceMoveIndex?: number;
  dateCreated: string;
  tags: string[];
}

export interface TestSuiteResult {
  testCaseId: string;
  variantKey: string;
  chosenMove: { from: string; to: string; description: string };
  evaluation: number;
  confidence: number;
  thinkingTime: number;
  isBlunder: boolean;
  reasoning?: string;
  timestamp: string;
}

// ── Storage Keys ──

const CASES_KEY = 'zodiac-blunder-test-suite';
const RESULTS_KEY = 'zodiac-blunder-test-results';

// ── Test Cases CRUD ──

export function getTestCases(): BlunderTestCase[] {
  try {
    const raw = localStorage.getItem(CASES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveTestCase(tc: BlunderTestCase): void {
  const cases = getTestCases();
  const idx = cases.findIndex(c => c.id === tc.id);
  if (idx >= 0) {
    cases[idx] = tc;
  } else {
    cases.push(tc);
  }
  localStorage.setItem(CASES_KEY, JSON.stringify(cases));
}

export function deleteTestCase(id: string): void {
  const cases = getTestCases().filter(c => c.id !== id);
  localStorage.setItem(CASES_KEY, JSON.stringify(cases));
  // Also remove associated results
  const results = getTestResults().filter(r => r.testCaseId !== id);
  localStorage.setItem(RESULTS_KEY, JSON.stringify(results));
}

// ── Test Results CRUD ──

export function getTestResults(testCaseId?: string): TestSuiteResult[] {
  try {
    const raw = localStorage.getItem(RESULTS_KEY);
    const all: TestSuiteResult[] = raw ? JSON.parse(raw) : [];
    return testCaseId ? all.filter(r => r.testCaseId === testCaseId) : all;
  } catch {
    return [];
  }
}

export function saveTestResult(result: TestSuiteResult): void {
  const results = getTestResults();
  results.push(result);
  localStorage.setItem(RESULTS_KEY, JSON.stringify(results));
}

export function clearTestResults(): void {
  localStorage.removeItem(RESULTS_KEY);
}

// ── Export / Import ──

export function exportTestSuite(): string {
  return JSON.stringify({
    cases: getTestCases(),
    results: getTestResults(),
    exportedAt: new Date().toISOString(),
  }, null, 2);
}

export function importTestSuite(json: string): { casesImported: number; resultsImported: number } {
  const data = JSON.parse(json);
  const cases: BlunderTestCase[] = data.cases || [];
  const results: TestSuiteResult[] = data.results || [];

  // Merge: add new cases (by id), skip duplicates
  const existing = getTestCases();
  const existingIds = new Set(existing.map(c => c.id));
  let casesImported = 0;
  for (const tc of cases) {
    if (!existingIds.has(tc.id)) {
      existing.push(tc);
      casesImported++;
    }
  }
  localStorage.setItem(CASES_KEY, JSON.stringify(existing));

  // Append all results
  const existingResults = getTestResults();
  const merged = [...existingResults, ...results];
  localStorage.setItem(RESULTS_KEY, JSON.stringify(merged));

  return { casesImported, resultsImported: results.length };
}

// ── Helpers ──

export function generateTestCaseId(): string {
  return `tc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Check if a move matches the known blunder move */
export function isMoveBlunder(
  testCase: BlunderTestCase,
  moveFrom: string,
  moveTo: string
): boolean | null {
  if (!testCase.blunderMove) return null; // No blunder move defined, can't auto-check
  return (
    testCase.blunderMove.from.toUpperCase() === moveFrom.toUpperCase() &&
    testCase.blunderMove.to.toUpperCase() === moveTo.toUpperCase()
  );
}

/** Get the most recent result for a test case + variant */
export function getLatestResult(
  testCaseId: string,
  variantKey?: string
): TestSuiteResult | null {
  const results = getTestResults(testCaseId);
  const filtered = variantKey
    ? results.filter(r => r.variantKey === variantKey)
    : results;
  return filtered.length > 0 ? filtered[filtered.length - 1] : null;
}
